import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { ShieldCheck, Star, Users } from "lucide-react";
import SEO from "@/components/SEO";
import { getYouTubeId } from "@/lib/videoUrl";

const IMG = "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/795385667/5364186985.jpg";

export default function About() {
  const { t, pick } = useLang();
  const [site, setSite] = useState({});
  useEffect(() => { api.get("/site").then((r) => setSite(r.data)); }, []);
  const heroYtId = getYouTubeId(site.hero_video_url);

  return (
    <div data-testid="about-page" className="bg-[#0D0D0D] min-h-screen">
      <SEO title="À propos · Spécialiste Porsche & voitures de prestige en Belgique" description="KV International à Rhode-Saint-Genèse — spécialiste de l'achat et vente de Porsche de prestige en Belgique. Dépôt-vente d'exception, coaching simulateur pro, 15 ans d'expertise, sélection rigoureuse." />
      {site.hero_video_url && (
        <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
          {heroYtId ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${heroYtId}?autoplay=1&mute=1&loop=1&playlist=${heroYtId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3`}
              title="Showroom KV International à Rhode-Saint-Genèse — voitures de luxe et Porsche de prestige"
              allow="autoplay; encrypted-media"
              data-testid="about-hero-video"
              className="absolute top-1/2 left-1/2 w-[max(100%,177.78vh)] h-[max(100%,56.25vw)] -translate-x-1/2 -translate-y-1/2 border-0 pointer-events-none"
            />
          ) : (
            <video autoPlay muted loop playsInline poster={IMG} className="absolute inset-0 w-full h-full object-cover img-fade" data-testid="about-hero-video" aria-label="Showroom KV International à Rhode-Saint-Genèse — voitures de luxe et Porsche de prestige">
              <source src={site.hero_video_url} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/50 via-[#0D0D0D]/60 to-[#0D0D0D]" />
          <div className="absolute inset-x-0 bottom-0 max-w-[1400px] mx-auto px-6 lg:px-10 pb-14">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#C8A75A]">Showroom · Rhode-Saint-Genèse</p>
          </div>
        </section>
      )}
      <div className={`${site.hero_video_url ? "pt-20" : "pt-40"} pb-32`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.35em] text-[#C8A75A] mb-6">03 — About</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95] font-light text-white">{t.about.title}</h1>
        <p className="text-white/60 text-xl mt-6 max-w-2xl font-light">{t.about.subtitle}</p>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-20 mt-24 items-start">
          <div className="relative aspect-[4/3] rounded-md overflow-hidden">
            <img src={IMG} alt="Showroom KV International — voitures de luxe et Porsche occasion prestige à Rhode-Saint-Genèse, Belgique" className="w-full h-full object-cover img-fade" />
          </div>
          <div>
            <p className="text-white/80 text-lg md:text-xl leading-[1.9] font-light whitespace-pre-line">{pick(site.about_text)}</p>

            <div className="grid grid-cols-3 gap-4 mt-16">
              <Stat n="15+" label="Années d'expertise" />
              <Stat n="500+" label="Clients satisfaits" />
              <Stat n="24/24" label="Disponibilité" />
            </div>

            <div className="mt-16 space-y-8">
              <Value icon={ShieldCheck} title="Confiance" text="Un seul interlocuteur, un discours clair, aucun compromis." />
              <Value icon={Star} title="Exigence" text="Sélection rigoureuse, vérifications approfondies, origine certifiée." />
              <Value icon={Users} title="Proximité" text="Un accompagnement humain, personnalisé, pensé sur le long terme." />
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-6 text-center">
      <p className="font-display text-4xl text-[#C8A75A]">{n}</p>
      <p className="text-white/50 text-xs uppercase tracking-widest mt-2">{label}</p>
    </div>
  );
}
function Value({ icon: Icon, title, text }) {
  return (
    <div className="flex gap-4">
      <Icon className="w-6 h-6 text-[#C8A75A] mt-1 flex-shrink-0" strokeWidth={1.3} />
      <div>
        <h4 className="font-display text-xl text-white">{title}</h4>
        <p className="text-white/60 text-sm mt-1">{text}</p>
      </div>
    </div>
  );
}
